import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  IconButton,
  Divider,
  Paper,
  Grid,
  StepContent,
  DialogActions,
  Slide,
  styled,
  useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import InvoiceService from '../../services/invoiceService';

// Step components
import ClientSelectionStep from './invoice-steps/ClientSelectionStep';
import ItemsStep from './invoice-steps/ItemsStep';
import ChargesDiscountsStep from './invoice-steps/ChargesDiscountsStep';
import InvoicePreviewStep from './invoice-steps/InvoicePreviewStep';

// Import IndexedDBService - REPLACED by InvoiceService
// import indexedDBService from '../../services/IndexedDBService';

// Custom transition for dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Custom styled components
const StepperContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const StepContentContainer = styled(Box)(({ theme }) => ({
  minHeight: 400,
  padding: theme.spacing(2),
}));

interface CreateInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  clients?: any[];
  inventory?: any[];
  businessDetails?: any;
  editMode?: boolean;
  initialInvoiceData?: any;
}

// Define interface for invoice item
interface InvoiceItem {
  id: number;
  name: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  inventoryItemId?: string;
  hsnCode?: string;
  gstRate?: number;
  taxAmount?: number;
}

// Define interface for client details with state property
interface InvoiceClientDetails {
  address: string;
  gstin: string;
  email: string;
  phone: string;
  state: string;
}

// Define interface for full invoice data
interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';

  // Client information
  client: string;
  clientId: string;
  clientDetails: InvoiceClientDetails;

  // Items
  items: InvoiceItem[];

  // Totals
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;

  // Additional information
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxable: boolean;

  // Metadata
  userId: string;
  createdAt: string;
}

// Steps for invoice creation
const steps = [
  'Client Information',
  'Items & Services',
  'Discounts & Charges',
  'Preview & Finalize'
];

const CreateInvoiceDialog: React.FC<CreateInvoiceDialogProps> = ({
  open,
  onClose,
  clients = [],
  inventory = [],
  businessDetails: propBusinessDetails,
  editMode = false,
  initialInvoiceData = null,
}) => {
  const theme = useTheme();
  const { currentUser } = useAuth();

  // State for business details if not provided in props
  const [businessDetails, setBusinessDetails] = useState(propBusinessDetails || {});
  const [loadingBusinessDetails, setLoadingBusinessDetails] = useState(!propBusinessDetails);

  // State for multi-step form
  const [activeStep, setActiveStep] = useState(0);
  const [confirmClose, setConfirmClose] = useState(false);

  // Generate initial invoice data based on editMode and initialInvoiceData
  const getInitialInvoiceData = () => {
    if (editMode && initialInvoiceData) {
      return {
        ...initialInvoiceData,
        // Ensure any potentially missing properties are set to defaults
        invoiceDate: initialInvoiceData.invoiceDate || initialInvoiceData.date || new Date().toISOString().split('T')[0],
        dueDate: initialInvoiceData.dueDate || new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: initialInvoiceData.status || 'draft',

        // Ensure client details object is properly structured
        clientDetails: {
          address: initialInvoiceData.clientDetails?.address || '',
          gstin: initialInvoiceData.clientDetails?.gstin || '',
          email: initialInvoiceData.clientDetails?.email || '',
          phone: initialInvoiceData.clientDetails?.phone || '',
          state: initialInvoiceData.clientDetails?.state || ''
        },

        // Make sure items have all required properties
        items: (initialInvoiceData.items || []).map((item: any) => ({
          id: item.id || Date.now() + Math.random(),
          name: item.name || '',
          description: item.description || '',
          quantity: item.quantity || 0,
          rate: item.rate || 0,
          amount: item.amount || 0,
          inventoryItemId: item.inventoryItemId || undefined,
          hsnCode: item.hsnCode || '',
          gstRate: item.gstRate || 0,
          taxAmount: item.taxAmount || 0
        })),

        // Set defaults for missing totals
        subtotal: initialInvoiceData.subtotal || 0,
        tax: initialInvoiceData.tax || 0,
        discount: initialInvoiceData.discount || 0,
        amount: initialInvoiceData.amount || 0,

        // Set defaults for missing additional information
        taxType: initialInvoiceData.taxType || 'gst',
        taxRate: initialInvoiceData.taxRate || 18,
        discountType: initialInvoiceData.discountType || 'fixed',
        discountValue: initialInvoiceData.discountValue || 0,
        taxable: initialInvoiceData.taxable !== undefined ? initialInvoiceData.taxable : true,

        // Make sure these required fields are present
        createdAt: initialInvoiceData.createdAt || new Date().toISOString(),
        userId: initialInvoiceData.userId || currentUser?.uid || '',

        // Add any additional fields that might be in initialInvoiceData
        ...initialInvoiceData
      };
    }

    // Return default data for new invoice
    return {
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'pending',

      // Client information
      client: '',
      clientId: '',
      clientDetails: {
        address: '',
        gstin: '',
        email: '',
        phone: '',
        state: ''
      },

      // Items
      items: [],

      // Totals
      subtotal: 0,
      tax: 0,
      discount: 0,
      amount: 0,

      // Additional information
      notes: '',
      termsAndConditions: '',
      taxType: 'gst',
      taxRate: 18,
      discountType: 'fixed' as 'percentage' | 'fixed',
      discountValue: 0,
      taxable: true,

      // Metadata
      userId: currentUser?.uid || '',
      createdAt: new Date().toISOString()
    };
  };

  // Invoice data state
  const [invoiceData, setInvoiceData] = useState<InvoiceData>(getInitialInvoiceData);

  // Initialize data whenever edit mode or initial data changes
  useEffect(() => {
    setInvoiceData(getInitialInvoiceData());
  }, [editMode, initialInvoiceData]);

  // Effect to generate invoice number
  useEffect(() => {
    // Generate invoice number format: INV-YYYYMMDD-XXX
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const randomPart = Math.floor(Math.random() * 1000).toString().padStart(3, '0');

    setInvoiceData(prev => ({
      ...prev,
      invoiceNumber: `INV-${year}${month}${day}-${randomPart}`
    }));
  }, []);

  // Effect to fetch business details if not provided in props
  useEffect(() => {
    const fetchBusinessDetails = async () => {
      if (propBusinessDetails || !currentUser?.uid || !open) {
        return;
      }

      setLoadingBusinessDetails(true);

      try {
        // Fetch from InvoiceService which now handles local DB fallbacks
        const businessData = await InvoiceService.fetchBusinessDetails(currentUser.uid);
        if (businessData) {
          setBusinessDetails(businessData);
        }
      } catch (error) {
        console.error('Error fetching business details:', error);
      } finally {
        setLoadingBusinessDetails(false);
      }
    };

    fetchBusinessDetails();
  }, [currentUser?.uid, open, propBusinessDetails]);

  // Handle step navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle dialog close with confirmation
  const handleDialogClose = () => {
    if (invoiceData.items.length > 0 || invoiceData.client) {
      setConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setConfirmClose(false);
    onClose();
  };

  const handleCancelClose = () => {
    setConfirmClose(false);
  };

  // Update invoice data from child components
  const updateInvoiceData = (data: Partial<InvoiceData>) => {
    setInvoiceData(prevData => ({ ...prevData, ...data }));
  };

  // Calculate the total amount whenever items change
  useEffect(() => {
    if (invoiceData.items && invoiceData.items.length > 0) {
      const subtotal = invoiceData.items.reduce((total, item) => total + item.amount, 0);
      let discount = 0;

      if (invoiceData.discountType === 'percentage') {
        discount = subtotal * (invoiceData.discountValue / 100);
      } else {
        discount = invoiceData.discountValue;
      }

      const taxableAmount = subtotal - discount;

      // Only calculate tax if GSTIN is provided and taxable is true
      const shouldCalculateTax = invoiceData.taxable && invoiceData.clientDetails.gstin && invoiceData.clientDetails.gstin.trim() !== '';
      const tax = shouldCalculateTax ? taxableAmount * (invoiceData.taxRate / 100) : 0;

      const total = taxableAmount + tax;

      setInvoiceData(prev => ({
        ...prev,
        subtotal,
        discount,
        tax,
        amount: total
      }));
    }
  }, [invoiceData.items, invoiceData.discountType, invoiceData.discountValue, invoiceData.taxRate, invoiceData.taxable, invoiceData.clientDetails.gstin]);

  // Handle save invoice - update to support edit mode
  const handleSaveInvoice = async (status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted' = 'draft') => {
    try {
      // Format the invoice data for saving
      const invoiceToSave = {
        ...invoiceData,
        status,
        template: 1, // Always use AkMo Invoice template (ID: 1)
        updatedAt: new Date().toISOString(),
        // Add missing required properties
        date: invoiceData.invoiceDate, // Use invoiceDate as date
        bankDetails: businessDetails?.bankDetails || {} // Use business bank details or empty object
      };

      // If editing, update the existing invoice
      if (editMode && initialInvoiceData?.id) {
        // Update the invoice using the InvoiceService
        await InvoiceService.updateInvoice(initialInvoiceData.id, invoiceToSave, currentUser?.uid || '');
      } else {
        // Save as new invoice
        const savedInvoice = await InvoiceService.createInvoice(invoiceToSave, currentUser?.uid || '');
      }

      // Close the dialog after saving
      onClose();
    } catch (error) {
      console.error('Error saving invoice:', error);
      // Handle error (show message to user)
    }
  };

  // Render current step
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ClientSelectionStep
            invoiceData={invoiceData}
            updateInvoiceData={updateInvoiceData}
            clients={clients}
            userId={currentUser?.uid || ''}
          />
        );
      case 1:
        return (
          <ItemsStep
            invoiceData={invoiceData}
            updateInvoiceData={updateInvoiceData}
            inventory={inventory}
            userId={currentUser?.uid || ''}
          />
        );
      case 2:
        return (
          <ChargesDiscountsStep
            invoiceData={invoiceData}
            updateInvoiceData={updateInvoiceData}
          />
        );
      case 3:
        return (
          <InvoicePreviewStep
            invoiceData={invoiceData}
            businessDetails={businessDetails}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  // Change the title based on whether we're editing or creating a new invoice
  const dialogTitle = editMode ? 'Edit Invoice' : 'Create New Invoice';

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        disableEscapeKeyDown
        aria-labelledby="create-invoice-dialog-title"
        onClose={handleDialogClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <DialogTitle id="create-invoice-dialog-title" sx={{ p: 0 }}>
          <HeaderContainer>
            <Box display="flex" alignItems="center">
              <ReceiptIcon sx={{ mr: 1 }} />
              <Typography variant="h6">{dialogTitle}</Typography>
            </Box>
            <IconButton edge="end" onClick={handleDialogClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </HeaderContainer>
        </DialogTitle>

        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="text.secondary">Invoice #</Typography>
              <Typography variant="body1" fontWeight="medium">{invoiceData.invoiceNumber}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="text.secondary">Date</Typography>
              <Typography variant="body1">{invoiceData.invoiceDate}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="text.secondary">Due Date</Typography>
              <Typography variant="body1">{invoiceData.dueDate}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{invoiceData.status}</Typography>
            </Grid>
          </Grid>
        </Box>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <StepperContainer>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </StepperContainer>

          <StepContentContainer sx={{ overflow: 'auto', flex: 1 }}>
            {renderStepContent(activeStep)}
          </StepContentContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>

          <Box sx={{ flex: 1 }} />

          {activeStep === steps.length - 1 ? (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={() => handleSaveInvoice('pending')}
                sx={{ mr: 1 }}
              >
                Save & Send
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                sx={{ mr: 1 }}
              >
                Print
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleSaveInvoice('draft')}
              >
                Save as Draft
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
            >
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirm close dialog */}
      <Dialog
        open={confirmClose}
        onClose={handleCancelClose}
        aria-labelledby="confirm-close-dialog"
      >
        <DialogTitle id="confirm-close-dialog">
          Discard changes?
        </DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to close and lose all changes?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmClose} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateInvoiceDialog; 